import { useNavigate } from "react-router-dom";
import { Smartphone } from "lucide-react";
import { formatRupiah, getPrimaryImage, stockInfo } from "@/lib/helpers";

const BADGE_STYLE = {
  Promo: "bg-rose-500 text-white",
  "Best Seller": "bg-amber-500 text-white",
  Terbaru: "bg-accent text-accent-foreground",
};

export default function ProductCard({ product }) {
  const navigate = useNavigate();
  const stock = stockInfo(product.stock_status);
  const soldOut = product.stock_status === "habis";
  const hasPromo = product.promo_price && product.promo_price < product.price;
  const discountPct = hasPromo ? Math.round((1 - product.promo_price / product.price) * 100) : 0;
  const models = product.compatible_models || [];

  return (
    <div
      data-testid={`product-card-${product.id}`}
      onClick={() => navigate(`/produk/${product.slug}`)}
      className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-200/60"
    >
      <div className="relative aspect-square overflow-hidden bg-secondary">
        <img
          src={getPrimaryImage(product)}
          alt={product.images?.[0]?.alt || product.name}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute left-2 top-2 flex flex-col gap-1">
          {(product.badges || []).map((b) => (
            <span key={b} className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${BADGE_STYLE[b] || "bg-primary text-primary-foreground"}`}>
              {b}
            </span>
          ))}
        </div>
        <span className={`absolute right-2 top-2 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${stock.cls}`}>
          {stock.label}
        </span>
      </div>

      <div className="flex flex-1 flex-col p-3 sm:p-4">
        {product.category_name && (
          <span className="text-[11px] font-semibold uppercase tracking-wider text-accent">{product.category_name}</span>
        )}
        <h3 data-testid={`product-title-${product.id}`} className="mt-0.5 font-heading text-sm sm:text-base font-semibold leading-snug line-clamp-2">
          {product.name}
        </h3>
        {models.length > 0 && (
          <p className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground line-clamp-1">
            <Smartphone className="h-3 w-3 shrink-0" />
            {models.slice(0, 2).map((m) => m.name).join(", ")}
            {models.length > 2 && ` +${models.length - 2}`}
          </p>
        )}
        <div className="mt-auto pt-2">
          <div className="flex flex-wrap items-center gap-1.5" data-testid={`product-price-${product.id}`}>
            <span className="font-heading text-base sm:text-lg font-bold text-foreground">
              {formatRupiah(hasPromo ? product.promo_price : product.price)}
            </span>
            {hasPromo && (
              <>
                <span className="text-xs text-muted-foreground line-through">{formatRupiah(product.price)}</span>
                <span data-testid={`product-discount-${product.id}`} className="rounded-full bg-rose-100 px-1.5 py-0.5 text-[10px] font-bold text-rose-600">
                  Hemat {discountPct}%
                </span>
              </>
            )}
          </div>
          <button
            data-testid={`product-cta-${product.id}`}
            className={`mt-2 w-full rounded-xl py-2 text-xs sm:text-sm font-semibold transition-colors ${
              soldOut ? "border border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100" : "bg-primary text-primary-foreground hover:opacity-90"
            }`}
          >
            {soldOut ? "Tanya Ketersediaan" : "Lihat Detail"}
          </button>
        </div>
      </div>
    </div>
  );
}
